import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, PlayCircle, Eye, Edit, Trash2, ExternalLink, Copy, Users, Activity, CalendarClock, XCircle, MoreHorizontal,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import { useBranch } from "../../contexts/BranchContext";
import CommonToaster from "../Common/CommonToaster";
import DataTable from "../Common/DataTable";
import {
  PageHeader, AppButton, ConfirmModal, SearchInput, EmptyState,
  StatusBadge, SelectControl, StatsCard, StatsGrid, ActionMenu, ActionMenuItem, ActionMenuDivider,
} from "../ui";
import DemoContextMenu from "./DemoContextMenu";
import { resolveSessionKind } from "./sessionKind";
import { can } from "../../utils/access";
import "./demo-classes.scss";

const PAGE_SIZE = 20;

// Demo-session states come from ui/statusVocabulary.js. The local
// STATUS_BADGE + STATUS_LABEL pair this replaces was one of THREE identical
// copies (the other two in DemoClasses/DemoClassesDetailPage and
// Dashboard/TrainerDashboard/cards/DemoSessionsCard), and all three mapped
// `live` to the success green while passing a `dot` that rendered nothing.

const formatDateTime = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleString(); } catch { return String(d); }
};

/**
 * The list page for BOTH live-session products. `kind` picks the endpoint, the
 * route tree and every visible word; `basePath` still overrides the route tree
 * for the instructor-scoped mounts (`/i/...`).
 */
const DemoClassesListPage = ({ kind = "demo", basePath, hideCreate = false }) => {
  const navigate = useNavigate();
  const { selectedBranchId } = useBranch();
  const vocab = resolveSessionKind(kind);
  const routeBase = basePath || vocab.adminBase;
  const canManage = !hideCreate && can("liveEvents.manage");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [contextMenu, setContextMenu] = useState(null); // { item, x, y }

  const openContextMenu = (item, e) => {
    e.preventDefault();
    setContextMenu({ item, x: e.clientX, y: e.clientY });
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, pageSize };
      if (search.trim())  params.q = search.trim();
      if (statusFilter)   params.status = statusFilter;
      const { data } = await apiClient.get(`${vocab.apiBase}/sessions`, { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch {
      setToast({ show: true, message: `Failed to load ${vocab.nounPlural}.`, type: "error" });
    }
    setLoading(false);
    // selectedBranchId triggers a re-fetch when the global branch is switched;
    // the X-Branch-Id header is attached automatically by apiClient.
  }, [page, pageSize, search, statusFilter, selectedBranchId, vocab]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSearchChange = (val) => { setSearch(val); setPage(1); };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiClient.delete(`${vocab.apiBase}/sessions/${deleteTarget._id}`);
      setToast({ show: true, message: `${vocab.FullNoun} removed.`, type: "success" });
      setDeleteTarget(null);
      fetchData();
    } catch (err) {
      setToast({ show: true, message: err?.response?.data?.message || "Failed to delete.", type: "error" });
    }
  };

  const copyPublicLink = (slug) => {
    const url = `${window.location.origin}${vocab.publicBase}/${slug}`;
    try {
      navigator.clipboard?.writeText(url);
      setToast({ show: true, message: "Public link copied to clipboard.", type: "success" });
    } catch {
      setToast({ show: true, message: url, type: "info" });
    }
  };

  const openPublic = (slug) => window.open(`${vocab.publicBase}/${slug}`, "_blank", "noopener");

  // Shared row actions — drives both the kebab dropdown and the right-click menu.
  const renderActionItems = (it) => (
    <>
      <ActionMenuItem onClick={() => navigate(`${routeBase}/${it._id}`)}>
        <Eye size={14} /> View
      </ActionMenuItem>
      <ActionMenuItem onClick={() => openPublic(it.publicSlug)}>
        <ExternalLink size={14} /> Open public page
      </ActionMenuItem>
      <ActionMenuItem onClick={() => copyPublicLink(it.publicSlug)}>
        <Copy size={14} /> Copy public link
      </ActionMenuItem>
      {canManage && (
        <ActionMenuItem onClick={() => navigate(`${routeBase}/${it._id}/edit`)}>
          <Edit size={14} /> Edit
        </ActionMenuItem>
      )}
      {canManage && <ActionMenuDivider />}
      {canManage && (
        <ActionMenuItem danger onClick={() => setDeleteTarget(it)}>
          <Trash2 size={14} /> Delete
        </ActionMenuItem>
      )}
    </>
  );

  const liveCount      = items.filter((i) => i.status === "live").length;
  const scheduledCount = items.filter((i) => i.status === "scheduled").length;
  const endedCount     = items.filter((i) => i.status === "ended").length;

  return (
    <div className="demo-classes-page">
      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />

      <PageHeader
        icon={PlayCircle}
        title={vocab.moduleTitle}
        description={vocab.copy.listDescription}
      />

      <StatsGrid className="demo-classes-page__stats">
        <StatsCard icon={Activity}      title="Live now"  value={liveCount}      iconColor="emerald" />
        <StatsCard icon={CalendarClock} title="Scheduled" value={scheduledCount} iconColor="blue" />
        <StatsCard icon={Users}         title="Total"     value={total}          iconColor="violet" />
        <StatsCard icon={XCircle}       title="Ended"     value={endedCount}     iconColor="rose" />
      </StatsGrid>

      <div className="demo-classes-page__toolbar">
        <div className="demo-classes-page__toolbar-left">
          <SearchInput value={search} onChange={handleSearchChange} placeholder="Search by title…" />
          <SelectControl
            label="Status"
            value={statusFilter}
            onChange={(v) => { setStatusFilter(v); setPage(1); }}
            options={[
              { value: "", label: "All statuses" },
              { value: "scheduled", label: "Scheduled" },
              { value: "live", label: "Live" },
              { value: "ended", label: "Ended" },
              { value: "cancelled", label: "Cancelled" },
            ]}
          />
        </div>

        {canManage && (
          <div className="demo-classes-page__toolbar-right">
            <AppButton variant="default" size="sm" onClick={() => navigate(`${routeBase}/new`)}>
              <Plus size={16} /> New {vocab.noun}
            </AppButton>
          </div>
        )}
      </div>

      {loading ? (
        <EmptyState title="Loading…" description={`Fetching your ${vocab.nounPlural}.`} />
      ) : items.length === 0 ? (
        <EmptyState
          icon={<PlayCircle size={42} />}
          title={vocab.copy.emptyTitle}
          description={vocab.copy.emptyDescription}
          action={
            canManage ? (
              <AppButton variant="default" onClick={() => navigate(`${routeBase}/new`)}>
                <Plus size={16} /> Create {vocab.noun}
              </AppButton>
            ) : undefined
          }
        />
      ) : (
        <>
          <DataTable
            data={items}
            columns={[
              {
                key: "title",
                label: "Title",
                render: (it) => (
                  <div className="demo-classes-page__title-cell">
                    <strong>{it.title}</strong>
                    <span className="demo-classes-page__title-cell-sub">
                      {it.instructorId?.name ? `By ${it.instructorId.name}` : "No instructor assigned"}
                      {it.batchId?.name ? ` · ${it.batchId.name}` : ""}
                    </span>
                  </div>
                ),
              },
              {
                key: "status",
                label: "Status",
                render: (it) => (
                  <StatusBadge status={it.status} domain="demoSession" />
                ),
              },
              { key: "scheduled", label: "Scheduled", render: (it) => formatDateTime(it.scheduledStartAt) },
              { key: "registered", label: "Registered", render: (it) => it.registrantCount || 0 },
              { key: "joined", label: "Joined", render: (it) => it.joinedCount || 0 },
              {
                key: "publicLink",
                label: "Public link",
                render: (it) => (
                  <button
                    type="button"
                    className="demo-classes-page__public-link"
                    onClick={() => copyPublicLink(it.publicSlug)}
                    title="Copy public link"
                  >
                    <Copy size={12} /> Copy link
                  </button>
                ),
              },
              {
                key: "actions",
                label: "",
                align: "end",
                className: "demo-classes-page__actions-cell",
                render: (it) => (
                  <div className="text-end">
                    <ActionMenu trigger={<MoreHorizontal size={16} />}>
                      {renderActionItems(it)}
                    </ActionMenu>
                  </div>
                ),
              },
            ]}
            onRowContextMenu={openContextMenu}
            pagination={{
              page,
              pageSize,
              total,
              onPageChange: setPage,
              onPageSizeChange: (size) => { setPageSize(size); setPage(1); },
              pageSizeOptions: [20, 50, 100],
            }}
          />
        </>
      )}

      <ConfirmModal
        show={!!deleteTarget}
        onHide={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title={`Delete this ${vocab.noun}?`}
        message={deleteTarget ? `Delete "${deleteTarget.title}"? Registrant data will remain in the database for reporting.` : ""}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      {/* Right-click row actions */}
      {contextMenu && (
        <DemoContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
        >
          {renderActionItems(contextMenu.item)}
        </DemoContextMenu>
      )}
    </div>
  );
};

export default DemoClassesListPage;
